import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Tab="CASES"|"PLAN"|"OUTCOME";

type Readiness={
  ready:boolean;
  livePlanCount?:number;
  openTaskCount?:number;
  completedPlanWithoutFinalOutcomeCount?:number;
  openExecutionCount?:number;
  finalOutcomeCount?:number;
  finalOutcomeSourceCount?:number;
};

type CaseItem={
  caseId:string;
  academicYearId:string;
  caseType:string;
  title:string;
  summary?:string|null;
  priority:string;
  status:string;
  ownerPersonId:string;
  ownerName:string;
  sourceRuleId?:string|null;
  version:number;
  createdAt:string;
  updatedAt:string;
  resolutionReadiness:Readiness;
};

type Goal={
  goalId:string;
  goalType:string;
  status:string;
  title:string;
  description?:string|null;
  targetOn?:string|null;
  effectiveAt:string;
};

type Plan={
  planId:string;
  caseId:string;
  goalId?:string|null;
  supersedesId?:string|null;
  status:string;
  planType:string;
  priority:string;
  title:string;
  description?:string|null;
  plannedFrom?:string|null;
  plannedUntil?:string|null;
  reviewDueAt?:string|null;
  successCriteria:unknown[];
  ownerPersonId:string;
  ownerName:string;
  version:number;
  effectiveAt:string;
  completedAt?:string|null;
  isCurrent:boolean;
  completionReadiness:Readiness;
};

type Execution={
  linkId:string;
  planId:string;
  status:string;
  resourceType:string;
  resourceId:string;
  title:string;
  resourceStatus:string;
  occurredAt?:string|null;
  linkedAt:string;
};

type Outcome={
  outcomeId:string;
  planId:string;
  reviewType:string;
  outcomeStatus:string;
  decision:string;
  summary:string;
  measureSnapshot:Record<string,unknown>;
  nextReviewAt?:string|null;
  reviewedByPersonId:string;
  reviewedByName:string;
  reviewedAt:string;
  sourceCount:number;
};

type OptionItem={
  id:string;
  title:string;
  status:string;
  occurredAt?:string|null;
};

type SourceItem={
  type:string;
  id:string;
  title:string;
  status:string;
  occurredAt?:string|null;
};

type Staff={personId:string;displayName:string};

type Case360={
  summary:{
    caseCount:number;
    openCaseCount:number;
    activePlanCount:number;
    openTaskCount:number;
    nextReviewAt?:string|null;
    achievedOutcomeCount:number;
  };
  cases:CaseItem[];
  goals:Goal[];
  plans:Plan[];
  executions:Execution[];
  outcomes:Outcome[];
  executionOptions:{
    counseling:OptionItem[];
    participations:OptionItem[];
    assessments:OptionItem[];
  };
  outcomeSources:SourceItem[];
  staff:Staff[];
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function dateInput(value?:string|null){
  return value ? value.slice(0,10) : "";
}

function dateTimeInput(value?:string|null){
  if(!value)return "";
  const date=new Date(value);
  const local=new Date(date.getTime()-date.getTimezoneOffset()*60000);
  return local.toISOString().slice(0,16);
}

function statusLabel(value:string){
  const map:Record<string,string>={
    OPEN:"열림",ACTIVE:"진행",WAITING:"대기",RESOLVED:"해결",CLOSED:"종료",
    DRAFT:"초안",PAUSED:"일시중지",COMPLETED:"완료",RETIRED:"이전 버전",
    IN_PROGRESS:"진행 중",BLOCKED:"차단",CANCELLED:"취소",
    VERIFIED:"검증",CONFIRMED:"확정",ATTENDED:"참여",REGISTERED:"등록",
    ASSIGNED:"배정",ACHIEVED:"달성",PROGRESSING:"진행 확인",
    NO_CHANGE:"변화 없음",ADJUST_NEEDED:"조정 필요",INSUFFICIENT_EVIDENCE:"근거 부족"
  };
  return map[value] || value;
}

function planTypeLabel(value:string){
  const map:Record<string,string>={
    CAREER_SUPPORT:"진로지원",ASSESSMENT_FOLLOWUP:"검사 후속",
    COUNSELING_FOLLOWUP:"상담 후속",EXPERIENCE_SUPPORT:"체험 지원",
    TRANSITION_SUPPORT:"전환 지원",POLICY_SUPPORT:"정책·지원",OTHER:"기타"
  };
  return map[value] || value;
}

function resourceTypeLabel(value:string){
  const map:Record<string,string>={
    CAREER_TASK:"진로업무",COUNSELING_SESSION:"상담",PARTICIPATION:"수업·활동",ASSESSMENT:"진로검사",EVIDENCE:"활동 증빙",EVALUATION:"평가 결과"
  };
  return map[value] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function StudentCareerCase360Panel({
  client,schoolId,learnerId
}:{
  client:PlatformClient;
  schoolId:string;
  learnerId:string;
}){
  const [data,setData]=useState<Case360|null>(null);
  const [tab,setTab]=useState<Tab>("CASES");
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [selectedCaseId,setSelectedCaseId]=useState("");

  const [caseType,setCaseType]=useState("PLANNING");
  const [casePriority,setCasePriority]=useState("NORMAL");
  const [caseTitle,setCaseTitle]=useState("");
  const [caseSummary,setCaseSummary]=useState("");

  const [goalType,setGoalType]=useState("EXPLORATION");
  const [goalTitle,setGoalTitle]=useState("");
  const [goalDescription,setGoalDescription]=useState("");
  const [goalTarget,setGoalTarget]=useState("");

  const [editingPlanId,setEditingPlanId]=useState("");
  const [planGoalId,setPlanGoalId]=useState("");
  const [planType,setPlanType]=useState("CAREER_SUPPORT");
  const [planPriority,setPlanPriority]=useState("NORMAL");
  const [planTitle,setPlanTitle]=useState("");
  const [planDescription,setPlanDescription]=useState("");
  const [planFrom,setPlanFrom]=useState("");
  const [planUntil,setPlanUntil]=useState("");
  const [planReviewAt,setPlanReviewAt]=useState("");
  const [planCriterion,setPlanCriterion]=useState("");
  const [planOwner,setPlanOwner]=useState("");

  const [taskTitle,setTaskTitle]=useState("");
  const [taskPriority,setTaskPriority]=useState("NORMAL");
  const [taskDue,setTaskDue]=useState("");
  const [taskAssignee,setTaskAssignee]=useState("");
  const [taskRationale,setTaskRationale]=useState("");

  const [existingExecution,setExistingExecution]=useState("");

  const [reviewType,setReviewType]=useState("CHECKPOINT");
  const [outcomeStatus,setOutcomeStatus]=useState("PROGRESSING");
  const [outcomeDecision,setOutcomeDecision]=useState("CONTINUE");
  const [outcomeSummary,setOutcomeSummary]=useState("");
  const [nextReviewAt,setNextReviewAt]=useState("");
  const [outcomeSource,setOutcomeSource]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_student_career_case_360",{
      p_school_id:schoolId,p_learner_id:learnerId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Case360;
      setData(next);
      setSelectedCaseId((current)=>
        next.cases.some((item)=>item.caseId===current)
          ? current
          : next.cases.find((item)=>!["RESOLVED","CLOSED"].includes(item.status))?.caseId
            || next.cases[0]?.caseId
            || ""
      );
      setPlanOwner((current)=>
        next.staff.some((item)=>item.personId===current)
          ? current
          : next.staff[0]?.personId || ""
      );
      setTaskAssignee((current)=>
        next.staff.some((item)=>item.personId===current)
          ? current
          : next.staff[0]?.personId || ""
      );
      setPlanGoalId((current)=>
        next.goals.some((item)=>item.goalId===current)
          ? current
          : next.goals.find((item)=>item.status==="ACTIVE")?.goalId || ""
      );
      setOutcomeSource((current)=>{
        const keySet=new Set(next.outcomeSources.map((item)=>item.type+"|"+item.id));
        return keySet.has(current) ? current : next.outcomeSources[0] ? next.outcomeSources[0].type+"|"+next.outcomeSources[0].id : "";
      });
    });
    return()=>{active=false;};
  },[client,schoolId,learnerId,refresh]);

  const selectedCase=useMemo(
    ()=>data?.cases.find((item)=>item.caseId===selectedCaseId) || null,
    [data,selectedCaseId]
  );

  const casePlans=useMemo(
    ()=>data?.plans.filter((item)=>item.caseId===selectedCaseId) || [],
    [data,selectedCaseId]
  );

  const currentPlan=useMemo(
    ()=>casePlans.find((item)=>item.isCurrent) || null,
    [casePlans]
  );

  const planExecutions=useMemo(
    ()=>data?.executions.filter((item)=>item.planId===currentPlan?.planId) || [],
    [data,currentPlan]
  );

  const planOutcomes=useMemo(
    ()=>data?.outcomes.filter((item)=>casePlans.some((plan)=>plan.planId===item.planId)) || [],
    [data,casePlans]
  );

  const executionOptions=useMemo(()=>{
    if(!data)return [];
    return [
      ...data.executionOptions.counseling.map((item)=>({...item,type:"COUNSELING_SESSION"})),
      ...data.executionOptions.participations.map((item)=>({...item,type:"PARTICIPATION"})),
      ...data.executionOptions.assessments.map((item)=>({...item,type:"ASSESSMENT"}))
    ];
  },[data]);

  useEffect(()=>{
    setExistingExecution((current)=>{
      const keys=new Set(executionOptions.map((item)=>item.type+"|"+item.id));
      return keys.has(current) ? current : executionOptions[0] ? executionOptions[0].type+"|"+executionOptions[0].id : "";
    });
  },[executionOptions]);

  async function run(key:string,action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  async function createCase(){
    if(!caseTitle.trim())return;
    const id=await run("case-create",()=>client.rpc("center_create_student_career_case",{
      p_school_id:schoolId,p_learner_id:learnerId,p_case_type:caseType,
      p_title:caseTitle.trim(),p_summary:caseSummary.trim() || null,
      p_priority:casePriority,p_source_rule_id:null
    }),"Student Career Case를 열었습니다.");
    if(id){
      setSelectedCaseId(String(id));
      setCaseTitle("");setCaseSummary("");
    }
  }

  async function transitionCase(toStatus:string){
    if(!selectedCase)return;
    await run("case-transition",()=>client.rpc("center_transition_student_career_case",{
      p_school_id:schoolId,p_case_id:selectedCase.caseId,
      p_expected_version:selectedCase.version,p_to_status:toStatus,
      p_note:"Career Case 360에서 상태 변경"
    }),toStatus==="RESOLVED"?"Case를 해결 상태로 전환했습니다.":toStatus==="CLOSED"?"Case를 종료했습니다.":"Case 상태를 변경했습니다.");
  }

  async function createGoal(){
    if(!goalTitle.trim())return;
    const id=await run("goal-create",()=>client.rpc("center_create_career_goal",{
      p_school_id:schoolId,p_learner_id:learnerId,p_goal_type:goalType,
      p_title:goalTitle.trim(),p_description:goalDescription.trim() || null,
      p_target_on:goalTarget || null,p_status:"ACTIVE"
    }),"진로목표를 만들었습니다.");
    if(id){
      setPlanGoalId(String(id));setGoalTitle("");setGoalDescription("");setGoalTarget("");
    }
  }

  function startPlanRevision(plan:Plan){
    setEditingPlanId(plan.planId);
    setPlanGoalId(plan.goalId || "");
    setPlanType(plan.planType);
    setPlanPriority(plan.priority);
    setPlanTitle(plan.title);
    setPlanDescription(plan.description || "");
    setPlanFrom(dateInput(plan.plannedFrom));
    setPlanUntil(dateInput(plan.plannedUntil));
    setPlanReviewAt(dateTimeInput(plan.reviewDueAt));
    setPlanCriterion(plan.successCriteria.map((item)=>typeof item==="string"?item:JSON.stringify(item)).join("\n"));
    setPlanOwner(plan.ownerPersonId);
  }

  function clearPlanForm(){
    setEditingPlanId("");
    setPlanTitle("");setPlanDescription("");setPlanFrom("");setPlanUntil("");
    setPlanReviewAt("");setPlanCriterion("");
  }

  async function savePlan(){
    if(!selectedCase || !planTitle.trim())return;
    const criteria=planCriterion.split("\n").map((item)=>item.trim()).filter(Boolean);
    if(editingPlanId && currentPlan && currentPlan.planId===editingPlanId){
      const id=await run("plan-save",()=>client.rpc("center_revise_case_action_plan",{
        p_school_id:schoolId,p_plan_id:editingPlanId,p_expected_version:currentPlan.version,
        p_title:planTitle.trim(),p_description:planDescription.trim() || null,
        p_plan_type:planType,p_priority:planPriority,p_planned_from:planFrom || null,
        p_planned_until:planUntil || null,p_review_due_at:planReviewAt?new Date(planReviewAt).toISOString():null,
        p_success_criteria:criteria
      }),"지원계획 수정본을 새 버전으로 만들었습니다.");
      if(id)clearPlanForm();
      return;
    }
    const id=await run("plan-save",()=>client.rpc("center_create_case_action_plan",{
      p_school_id:schoolId,p_case_id:selectedCase.caseId,p_goal_id:planGoalId || null,
      p_plan_type:planType,p_priority:planPriority,p_title:planTitle.trim(),
      p_description:planDescription.trim() || null,p_planned_from:planFrom || null,
      p_planned_until:planUntil || null,p_review_due_at:planReviewAt?new Date(planReviewAt).toISOString():null,
      p_success_criteria:criteria,p_owner_person_id:planOwner || null,p_initial_status:"ACTIVE"
    }),"진로지원계획을 시작했습니다.");
    if(id)clearPlanForm();
  }

  async function transitionPlan(toStatus:string){
    if(!currentPlan)return;
    await run("plan-transition",()=>client.rpc("center_transition_case_action_plan",{
      p_school_id:schoolId,p_plan_id:currentPlan.planId,
      p_expected_version:currentPlan.version,p_to_status:toStatus,
      p_note:"Career Case 360에서 계획 상태 변경"
    }),"지원계획 상태를 변경했습니다.");
  }

  async function createPlanTask(){
    if(!currentPlan || !taskTitle.trim())return;
    const result=await run("plan-task",()=>client.rpc("center_create_plan_career_task",{
      p_school_id:schoolId,p_plan_id:currentPlan.planId,p_title:taskTitle.trim(),
      p_priority:taskPriority,p_due_at:taskDue?new Date(taskDue).toISOString():null,
      p_assignee_person_id:taskAssignee || null,p_rationale:taskRationale.trim() || null
    }),"지원계획 실행업무를 만들었습니다.");
    if(result){setTaskTitle("");setTaskDue("");setTaskRationale("");}
  }

  async function linkExistingExecution(){
    if(!currentPlan || !existingExecution)return;
    const parts=existingExecution.split("|");
    await run("execution-link",()=>client.rpc("center_link_action_plan_execution",{
      p_school_id:schoolId,p_plan_id:currentPlan.planId,
      p_resource_type:parts[0],p_resource_id:parts[1]
    }),"기존 실행기록을 지원계획에 연결했습니다.");
  }

  async function retireExecution(linkId:string){
    await run("execution-retire",()=>client.rpc("center_retire_action_plan_execution",{
      p_school_id:schoolId,p_link_id:linkId
    }),"실행항목 연결을 종료했습니다.");
  }

  async function recordOutcome(){
    if(!currentPlan || !outcomeSummary.trim())return;
    const refs=outcomeSource ? [{
      type:outcomeSource.split("|")[0],id:outcomeSource.split("|")[1]
    }] : [];
    const result=await run("outcome",()=>client.rpc("center_record_action_plan_outcome",{
      p_school_id:schoolId,p_plan_id:currentPlan.planId,p_review_type:reviewType,
      p_outcome_status:outcomeStatus,p_decision:outcomeDecision,
      p_summary:outcomeSummary.trim(),p_measure_snapshot:{source:"TEACHER_REVIEW"},
      p_next_review_at:nextReviewAt?new Date(nextReviewAt).toISOString():null,
      p_source_refs:refs
    }),outcomeDecision==="COMPLETE_PLAN"||outcomeDecision==="CLOSE_CASE"?"최종 성과를 기록하고 지원계획을 완료했습니다.":"성과점검을 기록했습니다.");
    if(result){setOutcomeSummary("");setNextReviewAt("");}
  }

  if(!data)return (
    <section className="erp10-case360">
      {error ? <p className="error">{error}</p> : <p className="muted">Career Case 360을 불러오는 중…</p>}
    </section>
  );

  return (
    <section className="erp10-case360">
      <header className="erp10-head">
        <div>
          <p className="eyebrow">Career Case 360</p>
          <h3>학생 지원계획과 결과를 한 흐름으로 관리합니다</h3>
          <p>Case → 목표 → 지원계획 → 실행 → 성과점검 → 해결·종료</p>
        </div>
        <button className="secondary" onClick={()=>navigate("/assurance")}>업무안심 열기</button>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="erp10-metrics">
        <Metric label="열린 Case" value={data.summary.openCaseCount}/>
        <Metric label="활성 지원계획" value={data.summary.activePlanCount}/>
        <Metric label="미완료 진로업무" value={data.summary.openTaskCount}/>
        <Metric label="달성 Outcome" value={data.summary.achievedOutcomeCount}/>
        <div className="erp10-review-metric"><span>다음 점검</span><strong>{data.summary.nextReviewAt?fmt(data.summary.nextReviewAt):"미정"}</strong></div>
      </div>

      <div className="erp10-tabs">
        {([["CASES","Case·목표"],["PLAN","지원계획·실행"],["OUTCOME","성과점검·종료"]] as Array<[Tab,string]>).map(([value,label])=>(
          <button key={value} className={tab===value?"active":""} onClick={()=>setTab(value)}>{label}</button>
        ))}
      </div>

      {tab==="CASES" && (
        <div className="erp10-two">
          <section className="erp10-card">
            <div className="panel-title"><strong>Student Career Case</strong><span>{data.cases.length}건</span></div>
            <div className="erp10-case-list">
              {data.cases.map((item)=>(
                <button key={item.caseId} className={selectedCaseId===item.caseId?"active":""} onClick={()=>setSelectedCaseId(item.caseId)}>
                  <span><strong>{item.title}</strong><small>{item.caseType} · {item.ownerName}</small></span>
                  <b>{statusLabel(item.status)}</b>
                </button>
              ))}
              {!data.cases.length && <p className="case-empty">아직 Student Career Case가 없습니다.</p>}
            </div>
            {selectedCase && (
              <div className="erp10-case-detail">
                <strong>{selectedCase.title}</strong>
                {selectedCase.summary && <p>{selectedCase.summary}</p>}
                <div className="erp10-readiness">
                  <span>지원계획 {selectedCase.resolutionReadiness.livePlanCount || 0}</span>
                  <span>미완료 업무 {selectedCase.resolutionReadiness.openTaskCount || 0}</span>
                  <b>{selectedCase.resolutionReadiness.ready?"해결 가능":"진행 필요"}</b>
                </div>
                <div className="program-status-actions">
                  {selectedCase.status==="OPEN" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase("ACTIVE")}>진행 시작</button>}
                  {["OPEN","ACTIVE"].includes(selectedCase.status) && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase("WAITING")}>대기</button>}
                  {!["RESOLVED","CLOSED"].includes(selectedCase.status) && <button className="primary compact-button" disabled={busy!=="" || !selectedCase.resolutionReadiness.ready} onClick={()=>transitionCase("RESOLVED")}>Case 해결</button>}
                  {selectedCase.status==="RESOLVED" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionCase("CLOSED")}>Case 종료</button>}
                </div>
              </div>
            )}
          </section>

          <div className="erp10-stack">
            <section className="erp10-card">
              <div className="panel-title"><strong>새 Case 열기</strong><span>학생 중심 업무</span></div>
              <div className="erp10-form-two">
                <label>유형<select value={caseType} onChange={(e)=>setCaseType(e.target.value)}><option value="PLANNING">진로설계</option><option value="TRANSITION">전환</option><option value="ASSESSMENT">검사</option><option value="EXPERIENCE">체험</option><option value="COUNSELING">상담</option><option value="POLICY_SUPPORT">정책·지원</option><option value="OTHER">기타</option></select></label>
                <label>우선순위<select value={casePriority} onChange={(e)=>setCasePriority(e.target.value)}><option value="LOW">낮음</option><option value="NORMAL">일반</option><option value="HIGH">우선</option><option value="URGENT">긴급</option></select></label>
              </div>
              <label>제목<input value={caseTitle} onChange={(e)=>setCaseTitle(e.target.value)}/></label>
              <label>상황 요약<textarea rows={2} value={caseSummary} onChange={(e)=>setCaseSummary(e.target.value)}/></label>
              <button className="primary" disabled={busy!=="" || !caseTitle.trim()} onClick={createCase}>Case 열기</button>
            </section>

            <section className="erp10-card">
              <div className="panel-title"><strong>진로목표</strong><span>{data.goals.length}개</span></div>
              <div className="erp10-goals">
                {data.goals.slice(0,5).map((goal)=><div key={goal.goalId}><span><strong>{goal.title}</strong><small>{goal.goalType} · 목표 {goal.targetOn || "미정"}</small></span><b>{statusLabel(goal.status)}</b></div>)}
              </div>
              <div className="erp10-form-two">
                <label>목표 유형<select value={goalType} onChange={(e)=>setGoalType(e.target.value)}><option value="EXPLORATION">탐색</option><option value="LEARNING">학습</option><option value="EXPERIENCE">경험</option><option value="EDUCATION">진학</option><option value="CAREER">진로</option><option value="OTHER">기타</option></select></label>
                <label>목표일<input type="date" value={goalTarget} onChange={(e)=>setGoalTarget(e.target.value)}/></label>
              </div>
              <label>목표명<input value={goalTitle} onChange={(e)=>setGoalTitle(e.target.value)}/></label>
              <label>설명<input value={goalDescription} onChange={(e)=>setGoalDescription(e.target.value)}/></label>
              <button className="secondary" disabled={busy!=="" || !goalTitle.trim()} onClick={createGoal}>진로목표 추가</button>
            </section>
          </div>
        </div>
      )}

      {tab==="PLAN" && (
        <div className="erp10-two">
          <section className="erp10-card">
            <div className="panel-title"><strong>{editingPlanId?"지원계획 수정본":"진로지원계획"}</strong><span>ActionPlan · append-only revision</span></div>
            {!selectedCase ? <p className="case-empty">먼저 Case를 선택하세요.</p> : (
              <>
                <div className="erp10-form-two">
                  <label>목표<select value={planGoalId} onChange={(e)=>setPlanGoalId(e.target.value)}><option value="">목표 연결 안 함</option>{data.goals.filter((item)=>["DRAFT","ACTIVE"].includes(item.status)).map((goal)=><option key={goal.goalId} value={goal.goalId}>{goal.title}</option>)}</select></label>
                  <label>계획 유형<select value={planType} onChange={(e)=>setPlanType(e.target.value)}><option value="CAREER_SUPPORT">진로지원</option><option value="ASSESSMENT_FOLLOWUP">검사 후속</option><option value="COUNSELING_FOLLOWUP">상담 후속</option><option value="EXPERIENCE_SUPPORT">체험 지원</option><option value="TRANSITION_SUPPORT">전환 지원</option><option value="POLICY_SUPPORT">정책·지원</option><option value="OTHER">기타</option></select></label>
                  <label>우선순위<select value={planPriority} onChange={(e)=>setPlanPriority(e.target.value)}><option value="LOW">낮음</option><option value="NORMAL">일반</option><option value="HIGH">우선</option><option value="URGENT">긴급</option></select></label>
                  {data.staff.length>0 && <label>담당자<select value={planOwner} onChange={(e)=>setPlanOwner(e.target.value)}>{data.staff.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}</select></label>}
                  <label>시작일<input type="date" value={planFrom} onChange={(e)=>setPlanFrom(e.target.value)}/></label>
                  <label>종료 예정일<input type="date" value={planUntil} onChange={(e)=>setPlanUntil(e.target.value)}/></label>
                  <label>다음 점검<input type="datetime-local" value={planReviewAt} onChange={(e)=>setPlanReviewAt(e.target.value)}/></label>
                </div>
                <label>계획명<input value={planTitle} onChange={(e)=>setPlanTitle(e.target.value)}/></label>
                <label>지원 내용<textarea rows={3} value={planDescription} onChange={(e)=>setPlanDescription(e.target.value)}/></label>
                <label>성공기준<textarea rows={2} value={planCriterion} onChange={(e)=>setPlanCriterion(e.target.value)} placeholder="한 줄에 하나씩 입력"/></label>
                <div className="program-status-actions">
                  {editingPlanId && <button className="secondary" onClick={clearPlanForm}>수정 취소</button>}
                  <button className="primary" disabled={busy!=="" || !planTitle.trim() || (!editingPlanId && !!currentPlan && ["DRAFT","ACTIVE","PAUSED"].includes(currentPlan.status))} onClick={savePlan}>{editingPlanId?"수정본 생성":"지원계획 시작"}</button>
                </div>
              </>
            )}
          </section>

          <div className="erp10-stack">
            <section className="erp10-card">
              <div className="panel-title"><strong>현재 지원계획</strong><span>{currentPlan?"v"+currentPlan.version:"없음"}</span></div>
              {!currentPlan ? <p className="case-empty">선택한 Case의 지원계획이 없습니다.</p> : (
                <>
                  <div className="erp10-current-plan">
                    <header><div><strong>{currentPlan.title}</strong><span>{planTypeLabel(currentPlan.planType)} · {currentPlan.ownerName}</span></div><span className={currentPlan.status==="COMPLETED"?"status-pill good":"status-pill"}>{statusLabel(currentPlan.status)}</span></header>
                    {currentPlan.description && <p>{currentPlan.description}</p>}
                    <div className="erp10-readiness">
                      <span>열린 실행 {currentPlan.completionReadiness.openExecutionCount || 0}</span>
                      <span>최종 Outcome {currentPlan.completionReadiness.finalOutcomeCount || 0}</span>
                      <b>{currentPlan.completionReadiness.ready?"완료 가능":"진행 필요"}</b>
                    </div>
                    <div className="program-status-actions">
                      {["DRAFT","ACTIVE","PAUSED"].includes(currentPlan.status) && <button className="secondary" onClick={()=>startPlanRevision(currentPlan)}>계획 수정본</button>}
                      {currentPlan.status==="DRAFT" && <button className="primary compact-button" disabled={busy!==""} onClick={()=>transitionPlan("ACTIVE")}>계획 시작</button>}
                      {currentPlan.status==="ACTIVE" && <button className="secondary" disabled={busy!==""} onClick={()=>transitionPlan("PAUSED")}>일시중지</button>}
                      {currentPlan.status==="PAUSED" && <button className="primary compact-button" disabled={busy!==""} onClick={()=>transitionPlan("ACTIVE")}>재개</button>}
                      {["DRAFT","ACTIVE","PAUSED"].includes(currentPlan.status) && <button className="secondary danger" disabled={busy!==""} onClick={()=>transitionPlan("RETIRED")}>계획 종료</button>}
                    </div>
                  </div>
                  <details className="erp10-history">
                    <summary>계획 버전 이력 {casePlans.length}개</summary>
                    {casePlans.map((plan)=><div key={plan.planId}><span><b>v{plan.version} · {plan.title}</b><small>{fmt(plan.effectiveAt)}</small></span><strong>{plan.isCurrent?"현재":statusLabel(plan.status)}</strong></div>)}
                  </details>
                </>
              )}
            </section>

            {currentPlan && ["DRAFT","ACTIVE","PAUSED"].includes(currentPlan.status) && (
              <section className="erp10-card">
                <div className="panel-title"><strong>실행 연결</strong><span>Task·상담·활동·검사</span></div>
                <div className="erp10-execution-list">
                  {planExecutions.map((item)=><div key={item.linkId}><span><strong>{resourceTypeLabel(item.resourceType)} · {item.title}</strong><small>{statusLabel(item.resourceStatus)} · {fmt(item.occurredAt)}</small></span><button className="text-button" disabled={busy!=="" || item.status==="RETIRED"} onClick={()=>retireExecution(item.linkId)}>연결 종료</button></div>)}
                  {!planExecutions.length && <p className="case-empty">아직 연결된 실행항목이 없습니다.</p>}
                </div>
                <div className="erp10-form-two">
                  <label>새 진로업무<input value={taskTitle} onChange={(e)=>setTaskTitle(e.target.value)}/></label>
                  <label>우선순위<select value={taskPriority} onChange={(e)=>setTaskPriority(e.target.value)}><option value="NORMAL">일반</option><option value="HIGH">우선</option><option value="URGENT">긴급</option></select></label>
                  <label>기한<input type="datetime-local" value={taskDue} onChange={(e)=>setTaskDue(e.target.value)}/></label>
                  {data.staff.length>0 && <label>담당자<select value={taskAssignee} onChange={(e)=>setTaskAssignee(e.target.value)}>{data.staff.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}</select></label>}
                </div>
                <label>업무 이유<input value={taskRationale} onChange={(e)=>setTaskRationale(e.target.value)}/></label>
                <button className="primary" disabled={busy!=="" || !taskTitle.trim()} onClick={createPlanTask}>실행업무 생성</button>
                <div className="erp10-existing-link">
                  <select value={existingExecution} onChange={(e)=>setExistingExecution(e.target.value)}>
                    <option value="">기존 기록 선택</option>
                    {executionOptions.map((item)=><option key={item.type+"-"+item.id} value={item.type+"|"+item.id}>{resourceTypeLabel(item.type)} · {item.title} · {statusLabel(item.status)}</option>)}
                  </select>
                  <button className="secondary" disabled={busy!=="" || !existingExecution} onClick={linkExistingExecution}>기존 기록 연결</button>
                </div>
                <div className="erp10-link-outs">
                  <button className="text-button" onClick={()=>navigate("/classes?tab=counseling&student="+encodeURIComponent(learnerId))}>상담 열기</button>
                  <button className="text-button" onClick={()=>navigate("/programs")}>수업·활동 열기</button>
                  <button className="text-button" onClick={()=>navigate("/assessments")}>진로검사 열기</button>
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {tab==="OUTCOME" && (
        <div className="erp10-two">
          <section className="erp10-card">
            <div className="panel-title"><strong>성과점검</strong><span>Outcome Review</span></div>
            {!currentPlan || ["COMPLETED","RETIRED"].includes(currentPlan.status) ? (
              <p className="case-empty">활성 지원계획이 있을 때 새 성과점검을 기록할 수 있습니다.</p>
            ) : (
              <>
                <div className="erp10-form-two">
                  <label>점검 유형<select value={reviewType} onChange={(e)=>setReviewType(e.target.value)}><option value="BASELINE">초기기준</option><option value="CHECKPOINT">중간점검</option><option value="FINAL">최종점검</option></select></label>
                  <label>성과 상태<select value={outcomeStatus} onChange={(e)=>setOutcomeStatus(e.target.value)}><option value="NOT_MEASURED">미측정</option><option value="NO_CHANGE">변화 없음</option><option value="PROGRESSING">진행 확인</option><option value="ACHIEVED">달성</option><option value="ADJUST_NEEDED">조정 필요</option><option value="INSUFFICIENT_EVIDENCE">근거 부족</option></select></label>
                  <label>다음 결정<select value={outcomeDecision} onChange={(e)=>setOutcomeDecision(e.target.value)}><option value="CONTINUE">계속</option><option value="ADJUST">계획 조정</option><option value="REASSESS">재평가</option><option value="COMPLETE_PLAN">계획 완료</option><option value="CLOSE_CASE">Case 종료 준비</option></select></label>
                  <label>다음 점검<input type="datetime-local" value={nextReviewAt} onChange={(e)=>setNextReviewAt(e.target.value)}/></label>
                </div>
                <label>성과 근거<select value={outcomeSource} onChange={(e)=>setOutcomeSource(e.target.value)}><option value="">근거 연결 안 함</option>{data.outcomeSources.map((item)=><option key={item.type+"-"+item.id} value={item.type+"|"+item.id}>{resourceTypeLabel(item.type)} · {item.title} · {statusLabel(item.status)}</option>)}</select></label>
                <label>점검 요약<textarea rows={4} value={outcomeSummary} onChange={(e)=>setOutcomeSummary(e.target.value)}/></label>
                <button className="primary" disabled={busy!=="" || !outcomeSummary.trim() || ((outcomeDecision==="COMPLETE_PLAN"||outcomeDecision==="CLOSE_CASE") && (!outcomeSource || reviewType!=="FINAL" || !["PROGRESSING","ACHIEVED"].includes(outcomeStatus)))} onClick={recordOutcome}>성과점검 기록</button>
                {(outcomeDecision==="COMPLETE_PLAN"||outcomeDecision==="CLOSE_CASE") && <p className="muted">최종 완료는 실제 학생의 검증 Evidence·확정 평가·완료 상담·참여 활동·완료 검사 중 하나 이상의 근거가 필요합니다.</p>}
              </>
            )}
          </section>

          <section className="erp10-card">
            <div className="panel-title"><strong>Outcome Timeline</strong><span>{planOutcomes.length}건</span></div>
            <div className="erp10-outcome-list">
              {planOutcomes.map((item)=><article key={item.outcomeId}><header><div><strong>{statusLabel(item.outcomeStatus)}</strong><span>{item.reviewType} · {item.reviewedByName}</span></div><b>{item.decision}</b></header><p>{item.summary}</p><small>{fmt(item.reviewedAt)} · 근거 {item.sourceCount}개</small></article>)}
              {!planOutcomes.length && <p className="case-empty">아직 성과점검 기록이 없습니다.</p>}
            </div>
            {selectedCase && (
              <div className="erp10-final-gate">
                <strong>Case 종료 Gate</strong>
                <span>활성 계획 {selectedCase.resolutionReadiness.livePlanCount || 0} · 미완료 업무 {selectedCase.resolutionReadiness.openTaskCount || 0}</span>
                <b>{selectedCase.resolutionReadiness.ready?"해결 가능":"지원 흐름을 더 진행해야 합니다."}</b>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="erp10-metric"><span>{label}</span><strong>{value}</strong></div>;
}
